import type { ParsedLocalePath, PublishedLocaleManifest } from '@/i18n/types';

export interface ResolvePublishedLocaleInput {
    manifest: PublishedLocaleManifest;
    urlLocale?: string | null;
    savedLocale?: string | null;
    host?: string | null;
}

export interface RewritePathToPublishedLocaleInput {
    manifest: PublishedLocaleManifest;
    savedLocale?: string | null;
    host?: string | null;
    to: string;
}

const LOCALE_SEGMENT_PATTERN = /^[a-z]{2}(?:-[a-z]{2})?$/i;
const KNOWN_STALE_LOCALE_CODES = new Set(['es', 'fr']);

export function parseLocalePrefixedPath(pathname: string, localeCodes?: Iterable<string>): ParsedLocalePath {
    const normalizedPathname = normalizePathname(pathname);

    if (normalizedPathname === '/') {
        return { locale: null, remainderPathname: '/' };
    }

    const segments = normalizedPathname.split('/');
    const firstSegment = segments[1] ?? '';

    if (!isRecognizedLocaleSegment(firstSegment, localeCodes)) {
        return { locale: null, remainderPathname: normalizedPathname };
    }

    const remainder = normalizedPathname.slice(firstSegment.length + 1) || '/';

    return {
        locale: firstSegment.toLowerCase(),
        remainderPathname: remainder.startsWith('/') ? remainder : `/${remainder}`,
    };
}

export function resolvePublishedLocale(input: ResolvePublishedLocaleInput): string {
    const publishedLocaleCodes = new Set(input.manifest.locales.map(locale => locale.code));
    const urlLocale = normalizeLocale(input.urlLocale);

    if (urlLocale && publishedLocaleCodes.has(urlLocale)) {
        return urlLocale;
    }

    const savedLocale = normalizeLocale(input.savedLocale);

    if (savedLocale && publishedLocaleCodes.has(savedLocale)) {
        return savedLocale;
    }

    const hostLocale = input.host ? input.manifest.preferredHosts[input.host.toLowerCase()] : null;

    if (hostLocale && publishedLocaleCodes.has(hostLocale)) {
        return hostLocale;
    }

    return input.manifest.defaultLocale;
}

export function buildPublishedLocaleUrl(locale: string, pathname: string, search = '', hash = ''): string {
    const normalizedPathname = normalizePathname(pathname);
    const suffix = normalizedPathname === '/' ? '/' : normalizedPathname;

    return `/${locale}${suffix}${normalizeSearch(search)}${normalizeHash(hash)}`;
}

export function rewritePathToPublishedLocale(input: RewritePathToPublishedLocaleInput): string {
    const url = new URL(input.to, 'https://storkrentals.test');
    const publishedLocaleCodes = input.manifest.locales.map(locale => locale.code);
    const parsedPath = parseLocalePrefixedPath(url.pathname, [...publishedLocaleCodes, ...KNOWN_STALE_LOCALE_CODES]);
    const locale = resolvePublishedLocale({
        manifest: input.manifest,
        urlLocale: parsedPath.locale,
        savedLocale: input.savedLocale,
        host: input.host,
    });

    return buildPublishedLocaleUrl(locale, parsedPath.remainderPathname, url.search, url.hash);
}

function normalizeLocale(locale: string | null | undefined): string | null {
    if (!locale) {
        return null;
    }

    return locale.toLowerCase();
}

function normalizePathname(pathname: string): string {
    if (!pathname) {
        return '/';
    }

    return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

function normalizeSearch(search: string): string {
    if (!search) {
        return '';
    }

    return search.startsWith('?') ? search : `?${search}`;
}

function normalizeHash(hash: string): string {
    if (!hash) {
        return '';
    }

    return hash.startsWith('#') ? hash : `#${hash}`;
}

function isRecognizedLocaleSegment(segment: string, localeCodes?: Iterable<string>): boolean {
    if (!LOCALE_SEGMENT_PATTERN.test(segment)) {
        return false;
    }

    if (!localeCodes) {
        return true;
    }

    const normalizedSegment = segment.toLowerCase();

    for (const localeCode of localeCodes) {
        if (localeCode.toLowerCase() === normalizedSegment) {
            return true;
        }
    }

    return false;
}
